import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { allArticles } from '@/mocks/seoData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 40);
    }
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    }
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isHeroVisible = isHome && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Left: Navigation links */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                isHeroVisible
                  ? 'text-white hover:text-white/80'
                  : 'text-foreground-800 hover:text-primary-500'
              }`}
            >
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div ref={guideDropdownRef} className="relative">
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  isHeroVisible
                    ? 'text-white hover:text-white/80'
                    : 'text-foreground-800 hover:text-primary-500'
                }`}
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden max-h-80 overflow-y-auto">
                  {allArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setGuideDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                        <i className="ri-article-line text-primary-500 text-sm"></i>
                      </span>
                      <span className="leading-tight">{article.shortTitle}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  isHeroVisible
                    ? 'text-white hover:text-white/80'
                    : 'text-foreground-800 hover:text-primary-500'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-primary-500`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Center: Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-white text-base"></i>
            </div>
            <span className={`font-heading text-lg font-bold whitespace-nowrap transition-colors ${
              isHeroVisible ? 'text-white' : 'text-foreground-950'
            }`}>
              ItaobuySheet
            </span>
          </div>

          {/* Right: CTA buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                isHeroVisible
                  ? 'text-white border border-white/35 hover:bg-white/15'
                  : 'text-foreground-700 border border-foreground-300/40 hover:bg-foreground-50'
              }`}
            >
              Itaobuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line ml-1.5 text-xs"></i>
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`text-xl ${isHeroVisible ? 'text-white' : 'text-foreground-800'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col max-h-[75vh] overflow-y-auto">
          <Link
            to="/"
            className="text-sm font-semibold text-foreground-800 py-2.5 cursor-pointer"
            onClick={() => setMobileMenuOpen(false)}
          >
            <i className="ri-home-line mr-2 text-primary-500"></i>
            Home
          </Link>

          {/* Mobile Shopping Guide */}
          <button
            onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
            className="flex items-center justify-between text-sm font-semibold text-foreground-800 py-2.5 cursor-pointer w-full"
          >
            <span>
              <i className="ri-book-open-line mr-2 text-primary-500"></i>
              Shopping Guide
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {mobileGuideOpen && (
            <div className="ml-4 pl-3 border-l-2 border-primary-200 flex flex-col gap-1 mb-2">
              {allArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="text-sm text-foreground-600 py-1.5 cursor-pointer hover:text-primary-500 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {article.shortTitle}
                </Link>
              ))}
            </div>
          )}

          {/* Mobile Categories */}
          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between text-sm font-semibold text-foreground-800 py-2.5 cursor-pointer w-full"
          >
            <span>
              <i className="ri-grid-line mr-2 text-primary-500"></i>
              Categories
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {mobileCatOpen && (
            <div className="ml-4 pl-3 border-l-2 border-primary-200 flex flex-col gap-1 mb-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-sm text-foreground-600 py-1.5 cursor-pointer hover:text-primary-500 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-4 mt-2 border-t border-background-200/60">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 text-center px-4 py-2.5 text-sm font-medium text-foreground-700 border border-foreground-300/40 rounded-full hover:bg-foreground-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Itaobuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 text-center px-4 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}